import { MoreHorizontal, Trash2 } from 'lucide-react'

import type { IngredientName, Unit } from '@/api/types'
import { Combobox } from '@/components/Combobox'
import { emptyIngredientRow, type IngredientRow } from '@/components/editorRows'
import { SortableList } from '@/components/SortableList'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { fr } from '@/i18n/fr'

const NO_UNIT = '__none__'

interface Props {
  rows: IngredientRow[]
  units: Unit[]
  catalog: IngredientName[]
  onChange: (rows: IngredientRow[]) => void
}

/**
 * Two layouts, one component (NFR-18): a dense row from 640px, stacked cards below it. Kept
 * as a single JSX tree switched by Tailwind's `sm:` classes rather than two components, so
 * the two layouts cannot drift apart from each other.
 *
 * ```
 * ≥640px   [ farine                    ] [ 200 ] [ g ▾ ] [⋯] [⠿]
 *
 * <640px   ┌──────────────────────────────────────┐
 *          │  [ farine                ] [⋯] [⠿]   │
 *          │  [ 200 ] [ g ▾ ]                     │
 *          │  facultatif · reinettes              │  ← only when set
 *          └──────────────────────────────────────┘
 * ```
 *
 * Field order is **name → quantity → unit**: the name identifies the line and takes the
 * width, and the unit comes last because picking a known ingredient has usually filled it in
 * already (F-14). Only three controls stay on the row — the note, the optional flag and the
 * delete live behind `⋯` (F-17), because twenty rows each carrying six controls is what made
 * this screen unreadable. Their *values* still show, on the muted summary line: a note you
 * cannot see is a note you retype.
 *
 * Row identity is `row.id`, never the array index. Keying a sortable list by index reconciles
 * the wrong row the moment two of them swap, and dnd-kit cannot address the items at all.
 */
export function IngredientEditor({ rows, units, catalog, onChange }: Props) {
  function update(id: string, patch: Partial<IngredientRow>) {
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))
  }

  function remove(id: string) {
    onChange(rows.filter((row) => row.id !== id))
  }

  /**
   * Picking a known ingredient fills the unit in from the catalog (F-14) — but only into an
   * empty field. Someone who has already chosen "kg" meant it, and having it silently
   * replaced by the catalog's "g" on the way past is worse than no pre-fill at all.
   */
  function commitName(row: IngredientRow, name: string, match?: IngredientName) {
    const patch: Partial<IngredientRow> = { name }
    if (match?.default_unit && row.unit === '') patch.unit = match.default_unit
    update(row.id, patch)
  }

  const unitLabels = new Map(units.map((unit) => [unit.value, unit.label]))

  return (
    <div className="flex flex-col gap-3">
      <SortableList rows={rows} onReorder={onChange}>
        {(row, index, handle) => (
          <div className="rounded-xl border border-stone-200 p-3 dark:border-stone-800 sm:rounded-lg sm:border-0 sm:border-b sm:p-2">
            {/* One flex line that wraps below 640px and doesn't above it. The `order-*`
                utilities are what give the two layouts different arrangements of the same
                five controls without duplicating the JSX (NFR-18): on a phone the name
                shares a line with the two buttons and quantity+unit drop below; from 640px
                it is one dense row, name → quantity → unit → ⋯ → ⠿. */}
            <div className="flex flex-wrap items-start gap-2 sm:flex-nowrap">
              {/* The widest field on the row, because it is the one that identifies the
                  line — remark 4. Everything else qualifies it. */}
              <Combobox
                value={row.name}
                items={catalog}
                onChange={(name) => update(row.id, { name })}
                onCommit={(name, match) => commitName(row, name, match)}
                placeholder={fr.recipeEdit.ingredientNamePlaceholder}
                ariaLabel={fr.recipeEdit.ingredientNamePlaceholder}
                className="order-1 min-w-0 flex-1"
                renderHint={(item) =>
                  item.default_unit && (
                    <span className="shrink-0 text-xs text-stone-400">
                      {unitLabels.get(item.default_unit) ?? item.default_unit}
                    </span>
                  )
                }
              />

              <div className="order-4 flex w-full items-center gap-2 sm:order-3 sm:w-auto sm:shrink-0">
                <Input
                  inputMode="decimal"
                  value={row.quantity}
                  onChange={(event) => update(row.id, { quantity: event.target.value })}
                  placeholder={fr.recipeEdit.ingredientQuantityPlaceholder}
                  aria-label={fr.recipeEdit.ingredientQuantityPlaceholder}
                  className="w-20"
                />
                <UnitSelect
                  value={row.unit}
                  units={units}
                  onChange={(unit) => update(row.id, { unit })}
                />
              </div>

              {/* Menu then handle, in that order at both widths: the drag grip is the
                  outermost control on the row (NFR-19), so it stays in one place down a
                  whole list rather than moving with the row's content. */}
              <div className="order-2 shrink-0 sm:order-4">
                <RowOptions
                  row={row}
                  position={index + 1}
                  onChange={(patch) => update(row.id, patch)}
                  onRemove={() => remove(row.id)}
                />
              </div>
              <div className="order-3 shrink-0 sm:order-5">{handle}</div>
            </div>

            <RowSummary row={row} />
          </div>
        )}
      </SortableList>

      <Button
        type="button"
        variant="outline"
        onClick={() => onChange([...rows, emptyIngredientRow()])}
      >
        {fr.recipeEdit.addIngredient}
      </Button>
    </div>
  )
}

/**
 * What the row carries but no longer displays as a field (F-17). Renders nothing at all when
 * the row has neither a note nor the optional flag, which is the common case — so this line
 * appears exactly on the rows that have something to say.
 */
function RowSummary({ row }: { row: IngredientRow }) {
  const parts = [row.is_optional ? fr.common.optional : null, row.note.trim() || null].filter(
    Boolean,
  )
  if (parts.length === 0) return null

  return (
    <p className="mt-1.5 truncate text-xs text-stone-500 dark:text-stone-400">
      {parts.join(' · ')}
    </p>
  )
}

function RowOptions({
  row,
  position,
  onChange,
  onRemove,
}: {
  row: IngredientRow
  position: number
  onChange: (patch: Partial<IngredientRow>) => void
  onRemove: () => void
}) {
  const label = row.name.trim()
    ? fr.recipeEdit.ingredientOptions.named(row.name.trim())
    : fr.recipeEdit.ingredientOptions.unnamed(position)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          className="shrink-0 text-stone-400 hover:text-stone-900 dark:hover:text-stone-100"
        >
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`note-${row.id}`}>{fr.recipeEdit.ingredientOptions.noteLabel}</Label>
          <Input
            id={`note-${row.id}`}
            value={row.note}
            onChange={(event) => onChange({ note: event.target.value })}
            placeholder={fr.recipeEdit.ingredientNotePlaceholder}
          />
        </div>

        {/* Back to a full-size target: the reason it was shrunk was that it dominated the
            row, and in here there is room for it (invariant 9). */}
        <label className="flex min-h-touch cursor-pointer items-center gap-3 text-sm">
          <Checkbox
            checked={row.is_optional}
            onCheckedChange={(checked) => onChange({ is_optional: checked === true })}
          />
          {fr.recipeEdit.ingredientOptions.optionalLabel}
        </label>

        <Button
          type="button"
          variant="ghost"
          onClick={onRemove}
          className="justify-start gap-2 px-2 text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
        >
          <Trash2 className="h-4 w-4" />
          {fr.recipeEdit.ingredientOptions.remove}
        </Button>
      </PopoverContent>
    </Popover>
  )
}

function UnitSelect({
  value,
  units,
  onChange,
}: {
  value: string
  units: Unit[]
  onChange: (unit: string) => void
}) {
  return (
    <Select
      value={value === '' ? NO_UNIT : value}
      onValueChange={(next) => onChange(next === NO_UNIT ? '' : next)}
    >
      <SelectTrigger aria-label={fr.recipeEdit.ingredientUnitPlaceholder} className="w-28">
        <SelectValue placeholder={fr.recipeEdit.ingredientUnitPlaceholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_UNIT}>{fr.recipeEdit.noUnit}</SelectItem>
        {units.map((unit) => (
          <SelectItem key={unit.value} value={unit.value}>
            {unit.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
